import {
  getOnboardingDeclarationItems
} from '../types/membersTypes';
import { getGenderDisplayLabel, getRaceDisplayLabel } from './constants';
import { formatHeightInCm } from './index';

export type OnboardingDisplayRow = {
  label: string;
  value: string;
};

export type OnboardingDisplaySection = {
  title: string;
  rows: OnboardingDisplayRow[];
};

const toDisplay = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const cleaned = value.map((item) => String(item).trim()).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : '-';
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${v}`);
    return entries.length ? entries.join('; ') : '-';
  }
  const text = String(value).trim();
  return text || '-';
};

/** Empty string = No; non-empty = Yes + details (medical history pattern). */
const yesNoFromStoredDetail = (stored: unknown): string => {
  if (stored === undefined || stored === null) return '-';
  const text = String(stored).trim();
  if (!text) return 'No';
  return `Yes — ${text}`;
};

const formatEnumLabel = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const isOnboardingFormCompleted = (medicalCard: any): boolean => {
  const scanInfo = medicalCard?.scan_info;
  if (!scanInfo || typeof scanInfo !== 'object') return false;
  return Boolean(
    scanInfo.onboarding_declaration_confirmed || scanInfo.onboarding_form_completed_at
  );
};

export const buildOnboardingFormSections = (
  medicalCard: any,
  memberData: any,
  options?: { includeEmptyScanSection?: boolean; includeEmptyFemaleHormones?: boolean }
): OnboardingDisplaySection[] => {
  const basics = medicalCard?.basics || {};
  const health = medicalCard?.health_lifestyle || {};
  const nutritionist = medicalCard?.nutritionist_questions || {};
  const goals = medicalCard?.goals || {};
  const hormones = medicalCard?.female_sex_hormones || {};
  const scanInfo = medicalCard?.scan_info || {};
  const address = basics.address || {};
  const isFemale =
    String(memberData?.gender || basics.biological_sex || '').toLowerCase() === 'female';
  const isScanOrPlus = ['scan', 'plus'].includes(
    String(memberData?.membership_type || '').toLowerCase()
  );

  const sections: OnboardingDisplaySection[] = [
    {
      title: 'Personal Information',
      rows: [
        {
          label: 'Full name',
          value: toDisplay(
            `${memberData?.first_name || basics.first_name || ''} ${
              memberData?.last_name || basics.last_name || ''
            }`.trim()
          ),
        },
        {
          label: 'Date of Birth',
          value: toDisplay(memberData?.dob || basics.birth_date),
        },
        {
          label: 'Sex Assigned at Birth',
          value: getGenderDisplayLabel(memberData?.gender || basics.biological_sex),
        },
        {
          label: 'Weight',
          value: toDisplay(
            basics.weight != null ? `${basics.weight} ${basics.weight_unit || ''}`.trim() : null
          ),
        },
        {
          label: 'Height',
          value: formatHeightInCm(basics.height),
        },
        {
          label: 'Race/ethnicity (this can be relevant for your test results)',
          value: getRaceDisplayLabel(basics.race_ethnicity),
        },
        { label: 'Email', value: toDisplay(memberData?.email || basics.email) },
        {
          label: 'Mobile phone number',
          value: toDisplay(memberData?.phone || basics.phone),
        },
        {
          label: 'Address',
          value: toDisplay(address.full_address || address.address_line_1),
        },
        { label: 'City', value: toDisplay(address.city) },
        { label: 'Postal code', value: toDisplay(address.postcode) },
        { label: 'Country', value: toDisplay(address.country) },
      ],
    },
    {
      title: 'Current Lifestyle',
      rows: [
        {
          label: 'How would you describe your overall health?',
          value: formatEnumLabel(health.overall_health),
        },
        {
          label: 'How would you describe your activity level?',
          value: formatEnumLabel(health.activity_level),
        },
        {
          label: 'How would you describe your exercise routine?',
          value: toDisplay(nutritionist.exercise_routine),
        },
        {
          label: 'Please specify your exercise routine',
          value: toDisplay(nutritionist.other_exercise_routine),
        },
        {
          label: 'Do you smoke or use nicotine products?',
          value: toDisplay(health.smoking_status),
        },
        {
          label: 'How often do you drink alcohol?',
          value: toDisplay(health.alcohol_frequency),
        },
        {
          label: 'What best describes your working style?',
          value: toDisplay(nutritionist.working_style),
        },
        {
          label: 'Please specify your working style',
          value: toDisplay(nutritionist.other_working_style),
        },
        {
          label: 'Do you follow a particular diet?',
          value: toDisplay(nutritionist.diet_types),
        },
        {
          label: 'Please specify your diet type',
          value: toDisplay(nutritionist.other_diet_type),
        },
        {
          label: 'How would you describe your typical eating pattern?',
          value: toDisplay(nutritionist.eating_pattern),
        },
        {
          label: 'Please specify your eating pattern',
          value: toDisplay(nutritionist.other_eating_pattern),
        },
        {
          label: 'How do you usually hydrate?',
          value: toDisplay(nutritionist.hydration),
        },
        {
          label: 'Please specify your hydration method',
          value: toDisplay(nutritionist.other_hydration),
        },
        {
          label: 'Do you practise any calming or restorative activities?',
          value: toDisplay(nutritionist.calming_activities),
        },
        {
          label: 'Please specify your calming activity',
          value: toDisplay(nutritionist.other_calming_activity),
        },
      ].filter((row) => {
        if (row.label.startsWith('Please specify') && row.value === '-') return false;
        return true;
      }),
    },
    {
      title: 'Medical History',
      rows: [
        {
          label:
            'Do you have any past or ongoing medical conditions we should know about?',
          value: yesNoFromStoredDetail(health.medical_conditions),
        },
        {
          label: 'Have you had any previous surgeries or medical procedures?',
          value: yesNoFromStoredDetail(health.previous_surgeries),
        },
        {
          label: 'Are you currently taking any medications or supplements?',
          value: yesNoFromStoredDetail(health.supplements || health.medications),
        },
        {
          label:
            'Do you have any known allergies? If yes, please list the allergen and describe the reaction (e.g. penicillin, anaphylaxis; latex, skin reaction; contrast dye, nausea).',
          value: yesNoFromStoredDetail(health.allergies),
        },
      ],
    },
  ];

  if (
    isFemale &&
    (options?.includeEmptyFemaleHormones ||
      (hormones && Object.keys(hormones).length))
  ) {
    sections.push({
      title: 'Female Sex Hormones',
      rows: [
        {
          label: 'What was the date of your last menstrual period?',
          value: toDisplay(hormones.last_period_date),
        },
        {
          label:
            'Do you currently take hormonal contraception or HRT? If so, please provide the name and how long you have taken this for.',
          value: toDisplay(hormones.hormonal_contraception_hrt),
        },
        {
          label: 'Do you know your typical cycle length?',
          value: toDisplay(hormones.typical_cycle_length),
        },
        {
          label:
            'Primary reason for testing (e.g., PMS, fertility concerns, cycle mapping, fatigue)?',
          value: toDisplay(hormones.primary_testing_reason),
        },
        {
          label:
            'Menstrual flow characteristics (e.g., heavy, light, painful, presence of spotting)?',
          value: toDisplay(hormones.menstrual_flow_characteristics),
        },
        {
          label:
            'If postmenopausal, age at last menstrual period or whether surgical menopause occurred?',
          value: toDisplay(hormones.menopause_details),
        },
        {
          label:
            "Is there anything specific you'd like our clinical team to know before your assessment?",
          value: toDisplay(hormones.additional_notes_for_clinical_team),
        },
      ],
    });
  }

  if (isScanOrPlus && (scanInfo.reason_for_scan || scanInfo.medical_implants || options?.includeEmptyScanSection)) {
    const cancerAnswer =
      scanInfo.cancer_history === 'Yes' || scanInfo.cancer_history === 'yes'
        ? scanInfo.cancer_history_details
          ? `Yes — ${scanInfo.cancer_history_details}`
          : 'Yes'
        : scanInfo.cancer_history === 'No' || scanInfo.cancer_history === 'no'
          ? 'No'
          : toDisplay(scanInfo.cancer_history);

    sections.push({
      title: 'MRI Scan Safety and Clinical Considerations',
      rows: [
        {
          label: 'What is the primary reason for booking a full body MRI scan with us?',
          value: toDisplay(scanInfo.reason_for_scan),
        },
        {
          label:
            'Are you currently experiencing any symptoms such as pain, fatigue, or unexplained changes?',
          value: toDisplay(scanInfo.current_symptoms),
        },
        {
          label: 'Have you ever been diagnosed with cancer?',
          value: cancerAnswer,
        },
        {
          label:
            'Have you had previous medical imaging such as an MRI, CT, or X-ray? If yes, please describe where and approximately when.',
          value: toDisplay(scanInfo.any_previous_imaging),
        },
        {
          label:
            'Is there a family history of cancer, heart disease, or other serious conditions relevant to your reason for booking? If yes, please describe.',
          value: toDisplay(scanInfo.scan_family_history_details),
        },
        {
          label: 'Medical Implants and Metallic Objects - please select all that apply:',
          value: toDisplay(scanInfo.medical_implants),
        },
        {
          label:
            'If you selected any of the above, please give details — type, location in your body, and when it was inserted.',
          value: toDisplay(scanInfo.metallic_implants_details),
        },
        {
          label: 'Practical and Clinical Considerations - please select all that apply:',
          value: toDisplay(scanInfo.practical_considerations),
        },
        {
          label: 'If you selected any of the above, please give details:',
          value: toDisplay(scanInfo.practical_considerations_details),
        },
      ].filter((row) => {
        if (
          (row.label.startsWith('If you selected') ||
            row.label.includes('please give details')) &&
          row.value === '-'
        ) {
          return false;
        }
        return true;
      }),
    });
  }

  sections.push({
    title: 'Goals',
    rows: [
      {
        label: 'What brings you to CoreVitals today?',
        value: toDisplay(goals.goals),
      },
      {
        label: "Do you have any health concerns you'd like to explore further?",
        value: toDisplay(goals.health_concerns),
      },
      {
        label: 'If you selected any of the above, please give details:',
        value: toDisplay(goals.health_concern_details),
      },
      {
        label: 'When did you last have a blood test?',
        value: toDisplay(goals.last_blood_test),
      },
      {
        label:
          "Is there anything specific you'd like our clinical team to know before your assessment?",
        value: toDisplay(goals.additional_info),
      },
    ].filter((row) => {
      if (row.label.startsWith('If you selected') && row.value === '-') return false;
      return true;
    }),
  });

  return sections;
};

export const getOnboardingDisclaimerItemsForMember = (
  gender?: string | null,
  membershipType?: string | null
): string[] => {
  const isFemale = String(gender || '').toLowerCase() === 'female';
  return getOnboardingDeclarationItems(isFemale, membershipType);
};

export const isOnboardingDisclaimerItemConfirmed = (
  scanInfo: any,
  item: string
): boolean => {
  const confirmations = scanInfo?.disclaimer_confirmations;
  if (!confirmations || typeof confirmations !== 'object') return false;
  return Boolean(confirmations[item]);
};
